import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseEvents, { type AddCaseEventValue } from '../../../../hooks/useLegalCaseEvents';
import type { CaseEvent } from '../../../../types/LegalCase';
import LegalCaseCaseEventForm from './LegalCaseCaseEventForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseCaseEventsProps {
    caseId: string;
    initialEvents?: CaseEvent[];
}

// Converte a data do fato para um Date LOCAL à meia-noite. O backend manda `eventDate` como
// data pura (YYYY-MM-DD) ou datetime ISO; `new Date("YYYY-MM-DD")` seria interpretado como
// UTC e, num fuso negativo (ex.: UTC-3), deslocaria o dia — o selo "Agendado" acenderia
// errado na virada. Extrai só a parte da data e monta um Date local (ano, mês, dia), estável
// entre fusos. Mesmo approach do DateField (isoToDate).
const factDateLocal = (eventDate: string): Date | null => {
    const match = eventDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
};

// Um andamento é "agendado" quando a data do fato ainda não chegou (ex.: audiência marcada).
// Derivado da data na renderização — sem estado persistido; o selo some sozinho no próximo
// render depois que a data chega (compara só a data, ignora a hora).
const isScheduled = (eventDate: string | null): boolean => {
    if (!eventDate) return false;
    const fact = factDateLocal(eventDate);
    if (!fact) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return fact.getTime() > today.getTime();
};

// Uma linha da timeline. Extraída para fora do componente (diretriz: sem função dentro de
// função) e recebe o handler de remoção já ligado ao id.
const CaseEventItem = ({
    event,
    onRemove,
    disabled,
}: {
    event: CaseEvent;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="relative pl-6">
        {/* marcador da timeline — âmbar quando agendado (data do fato ainda no futuro) */}
        <span className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${isScheduled(event.eventDate) ? 'bg-amber-500' : 'bg-blue-500'}`} />
        <span className="absolute left-[4px] top-4 bottom-0 w-px bg-gray-200 dark:bg-slate-700" />
        <div className="flex items-start justify-between gap-2 pb-4">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                        {event.eventTypeFormatted}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {event.eventDate ? formatDate(event.eventDate) : '—'}
                    </span>
                    {isScheduled(event.eventDate) && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                            Agendado
                        </span>
                    )}
                    {event.sheetReference && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                            {event.sheetReference}
                        </span>
                    )}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200 mt-1 whitespace-pre-wrap break-words">
                    {event.text}
                </p>
                {event.author && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        Registrado por {event.author}
                    </p>
                )}
            </div>
            <button
                onClick={onRemove}
                disabled={disabled}
                title="Remover andamento"
                className="flex-shrink-0 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
            >
                <FaTrash size={11} />
            </button>
        </div>
    </li>
);

// Andamento processual de um caso: timeline ordenada pela data do fato (a ordenação vem do
// backend) com o formulário de registro. Substitui a coluna RESUMO da planilha (log em
// texto corrido). A regra de ordenação vive no backend — a tela só renderiza a lista.
const LegalCaseCaseEvents = ({ caseId, initialEvents }: LegalCaseCaseEventsProps) => {
    const {
        events, isSaving, errorFields, typeOptions,
        addEvent, removeEvent, clearErrors,
    } = useLegalCaseEvents(caseId, initialEvents);
    const [showForm, setShowForm] = useState(false);
    // Id do andamento pendente de confirmação de remoção (null = diálogo fechado).
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openForm = () => {
        clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        clearErrors();
        setShowForm(false);
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await removeEvent(pendingRemoval);
        setPendingRemoval(null);
    };

    const handleSubmit = async (value: AddCaseEventValue): Promise<boolean> => {
        const ok = await addEvent(value);
        if (ok) closeForm();
        return ok;
    };

    return (
        <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Andamento processual
                </h4>
                {!showForm && (
                    <button
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Registrar
                    </button>
                )}
            </div>

            {events.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum andamento registrado</p>
            )}

            {events.length > 0 && (
                <ul className="mt-1">
                    {events.map((event) => (
                        <CaseEventItem
                            key={event.id}
                            event={event}
                            onRemove={() => setPendingRemoval(event.id)}
                            disabled={isSaving}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCaseCaseEventForm
                    typeOptions={typeOptions}
                    errorFields={errorFields}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover andamento"
                message="Remover este andamento da timeline? Esta ação não pode ser desfeita."
                isBusy={isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseCaseEvents;
